import React, { useState } from 'react';
import styled from 'styled-components';
import { useWindows } from '../../contexts/WindowContext';
import AboutApp from '../Applications/AboutApp/AboutApp';
import TerminalApp from '../Applications/Terminal/TerminalApp';
import WallpaperManager from './WallpaperManager';
import SystemSettings from './SystemSettings';

const ContextMenuContainer = styled.div<{ visible: boolean; x: number; y: number }>`
  position: fixed;
  top: ${props => props.y}px;
  left: ${props => props.x}px;
  background: rgba(0, 0, 0, 0.95);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 8px;
  padding: 8px 0;
  min-width: 180px;
  z-index: 2000;
  display: ${props => props.visible ? 'block' : 'none'};
  backdrop-filter: blur(15px);
  box-shadow: 0 8px 32px rgba(0, 255, 0, 0.2);
`;

const MenuItem = styled.div`
  padding: 12px 20px;
  color: #00ff00;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 255, 0, 0.15);
    color: #00ffff;
    padding-left: 24px;
  }
`;

const MenuSeparator = styled.div`
  height: 1px;
  background: rgba(0, 255, 0, 0.2);
  margin: 4px 0;
`;

const ContextMenu: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const { openWindow } = useWindows();

  React.useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      
      // Adjust position to keep menu on screen
      const menuWidth = 180;
      const menuHeight = 200;
      let x = e.clientX;
      let y = e.clientY;
      
      if (x + menuWidth > window.innerWidth) {
        x = window.innerWidth - menuWidth - 10;
      }
      if (y + menuHeight > window.innerHeight) {
        y = window.innerHeight - menuHeight - 10;
      }
      
      setPosition({ x, y });
      setVisible(true);
    };

    const handleClick = () => {
      setVisible(false);
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setVisible(false);
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleOpenTerminal = (e: React.MouseEvent) => {
    e.stopPropagation();
    openWindow({
      title: 'Terminal Emulator',
      component: TerminalApp,
      isMinimized: false,
      isMaximized: false,
      position: { x: 100, y: 100 },
      size: { width: 800, height: 600 }
    });
    setVisible(false);
  };

  const handleChangeWallpaper = (e: React.MouseEvent) => {
    e.stopPropagation();
    openWindow({
      title: 'Wallpaper Settings',
      component: WallpaperManager,
      isMinimized: false,
      isMaximized: false,
      position: { x: 200, y: 100 },
      size: { width: 600, height: 500 }
    });
    setVisible(false);
  };

  const handleSystemSettings = (e: React.MouseEvent) => {
    e.stopPropagation();
    openWindow({
      title: 'System Settings',
      component: SystemSettings,
      isMinimized: false,
      isMaximized: false,
      position: { x: 150, y: 80 },
      size: { width: 900, height: 650 }
    });
    setVisible(false);
  };

  const handleAboutPortfolio = (e: React.MouseEvent) => {
    e.stopPropagation();
    openWindow({
      title: 'About Mukul Joshi',
      component: AboutApp,
      isMinimized: false,
      isMaximized: false,
      position: { x: 150, y: 150 },
      size: { width: 700, height: 500 }
    });
    setVisible(false);
  };

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.location.reload();
    setVisible(false);
  };

  return (
    <ContextMenuContainer visible={visible} x={position.x} y={position.y}>
      <MenuItem onClick={handleOpenTerminal}>
        🖥️ Open Terminal
      </MenuItem>
      <MenuSeparator />
      <MenuItem onClick={handleChangeWallpaper}>
        🎨 Change Wallpaper
      </MenuItem>
      <MenuItem onClick={handleSystemSettings}>
        ⚙️ System Settings
      </MenuItem>
      <MenuSeparator />
      <MenuItem onClick={handleAboutPortfolio}>
        ℹ️ About Portfolio
      </MenuItem>
      <MenuItem onClick={handleRefresh}>
        🔄 Refresh Desktop
      </MenuItem>
    </ContextMenuContainer>
  );
};

export default ContextMenu;
